import { motion } from "framer-motion";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Link } from "react-router-dom";
import { products } from "../data/products";
import { ArrowRight } from "lucide-react";
import { SEO } from "../components/SEO";

export default function ProductsPage() {
  // Group products by category - sin kiosques
  const categories = [
    { key: "terrasse", label: "Cailleboti, terrasses (Deck)" },
    { key: "bardage", label: "Bardage" },
    { key: "tasseaux", label: "Tasseaux" },
    { key: "parquet", label: "Parquets" },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen bg-[#F5F5F0]"
    >
      <Navbar />
      <SEO
        path="/nos-produits"
        title="Nos Produits — Terrasses, Bardages, Parquets en Bambou"
        description="Découvrez notre gamme complète de terrasses, bardages, tasseaux et parquets en bambou REBO. Produits haute résistance adaptés au climat tropical de La Réunion. Livraison sur toute l'île."
      />
      <main className="pt-24">
        {/* Hero */}
        <section className="py-16 md:py-24 bg-[#2D2D2D]">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#E8E8E8] mb-4"
              style={{ fontFamily: "'DM Serif Display', serif" }}
              data-testid="products-page-title"
            >
              Nos <span className="text-[#8BC34A]">produits</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-lg text-[#9E9E9E] max-w-2xl mx-auto"
            >
              Terrasses, bardages, parquets, revêtement mural et tasseaux : 
              découvrez notre gamme complète en bambou tressé carbonisé.
            </motion.p>
          </div>
        </section>

        {/* Products by Category */}
        <section className="py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            {categories.map((category, catIndex) => {
              const categoryProducts = products.filter(p => p.category === category.key);
              if (categoryProducts.length === 0) return null;

              return (
                <motion.div
                  key={category.key}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: catIndex * 0.1 }}
                  className="mb-16 last:mb-0"
                >
                  <h2
                    className="text-2xl md:text-3xl font-bold text-[#2D2D2D] mb-8 pb-4 border-b-2 border-[#8BC34A]"
                    style={{ fontFamily: "'DM Serif Display', serif" }}
                  >
                    {category.label}
                  </h2>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                    {categoryProducts.map((product, index) => (
                      <motion.div
                        key={product.id}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                        className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 border border-[#2D2D2D]/10"
                        data-testid={`product-card-${product.slug}`}
                      >
                        <div className="aspect-square overflow-hidden">
                          <img
                            src={product.image}
                            alt={product.name}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                          />
                        </div>
                        <div className="p-6">
                          <h3
                            className="text-xl font-bold text-[#2D2D2D] mb-2"
                            style={{ fontFamily: "'DM Serif Display', serif" }}
                          >
                            {product.name}
                          </h3>
                          <p className="text-sm text-[#8BC34A] font-medium mb-3">
                            {product.dimensions}
                          </p>
                          <Link
                            to={`/produit/${product.slug}`}
                            className="inline-flex items-center gap-2 text-[#2D2D2D] font-semibold hover:text-[#8BC34A] transition-colors"
                          >
                            En savoir plus
                            <ArrowRight className="w-4 h-4" />
                          </Link>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </section>
      </main>
      <Footer />
    </motion.div>
  );
}
